import MapboxDraw, { constants, DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import booleanIntersects from "@turf/boolean-intersects";
import { Feature } from "geojson";
import { uniqBy } from "lodash";
import { Point } from "mapbox-gl";

import * as turf from "fond/turf";

import readOnlySelect from "../base/readOnlySelect";

import { NBN_SOURCE, NBN_SOURCE_LAYER, NBN_SOURCE_LAYER_FILTER } from "./constants";

const COLLOCATED_FEATURE_BUFFER_METERS = 1;

const {
  lib: {
    CommonSelectors: { isFeature, noTarget, isOfMetaType, isShiftDown },
  },
} = MapboxDraw;

/**
 * Returns a Point representing a mouse event's position
 * relative to a containing element.
 *
 * @param {MouseEvent} mouseEvent
 * @param {Node} container
 * @returns {Point}
 */
function mouseEventPoint(mouseEvent: any, container: any) {
  const rect = container.getBoundingClientRect();
  return new Point(mouseEvent.clientX - rect.left - (container.clientLeft || 0), mouseEvent.clientY - rect.top - (container.clientTop || 0));
}

/**
 * This is a custom draw mode that allows for the user to select existing draw features on the map
 * or to select features from the NBN "input_layers/base_data/sl" layer.
 *
 * Selection of NBN layer features is done either by clicking on the feature or by box selecting.
 *
 * Selected features cannot be moved in this mode.
 */
const nbnSelect: DrawCustomMode = {
  ...readOnlySelect,

  /**
   * This is a custom onClick function to handle the creation of drawFeatures when clicking on a feature that
   * is sourced from a vector layer (i.e. not an existing draw feature)
   */
  onClick: function (state, event) {
    // Due to features within a vector tile project noTarget could mean the user
    // clicked on an empty area of the map or clicked on a feature within a vector tile layer.
    if (noTarget(event)) {
      // If no feature is targeted and shift is not held down, clear the selection
      if (!isShiftDown(event)) {
        this.clearSelectedFeatures();
      }

      // Determine if the user did in fact click on a feature within the NBN vector tile layer
      const vectorFeatures = uniqBy(this.getFeatures(event), "id");

      // Create the new drawing feature for each vector tile feature found, then select them.
      const newFeatures = vectorFeatures.map((feature) => {
        return this.addDrawFeature(feature);
      });
      newFeatures.forEach((f) => this.select(f.id));

      // If no new features were created, treat this as a clickAnywhere event (which may clear selection)
      if (newFeatures.length === 0) {
        return this.clickAnywhere(state, event);
      }
    }

    if (isOfMetaType(constants.meta.VERTEX)(event)) return this.clickOnVertex(state, event); // tap
    if (isFeature(event) && this.map) {
      // NBN commonly has collocated features, so we need to select all nearby features on click
      // based on the location of the top feature clicked.
      const point = event.featureTarget.geometry.coordinates;

      // We need to use querySourceFeatures here to get all features as queryRenderedFeatures does
      // not include features not rendered due to styling (ignore-placement, visibility, etc).
      const sourceFeatures = [...this.map.querySourceFeatures("mapbox-gl-draw-cold"), ...this.map.querySourceFeatures("mapbox-gl-draw-hot")];

      // However, querySourceFeatures does not support bbox querying, so we need to deduplicate and spatially filter ourselves
      const uniqueFeaturesInBbox = sourceFeatures.reduce<Feature[]>((acc, feature) => {
        // Deduplicate using feature id
        if (!acc.some((f) => f.properties?.id === feature.properties?.id)) {
          // Spatial filter using Turf and a small buffer around the clicked point
          if (booleanIntersects(feature, turf.buffer(turf.point(point), COLLOCATED_FEATURE_BUFFER_METERS, { units: "meters" }))) {
            acc.push(feature);
          }
        }
        return acc;
      }, []);

      // Select all the features found
      uniqueFeaturesInBbox.forEach((f) => {
        this.select(f.properties?.id);
      });
    }

    return undefined;
  },

  /**
   * A custom onMouseUp to handle the creation of drawFeatures when box selecting
   */
  onMouseUp: function (state, event) {
    // End any extended interactions
    if (state.dragMoving) {
      this.fireUpdate();
    } else if (state.boxSelecting) {
      // Check if the user has included within the box selection any features
      // within the "input_layers/base_data/sl" layer & if so create those draw features
      // and add them to the drawControl
      const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        state.boxSelectStartLocation,
        mouseEventPoint(event.originalEvent, this.map.getContainer()),
      ];
      const vectorFeatures = this.getFeatures(event, bbox);
      vectorFeatures.forEach((feature) => {
        this.addDrawFeature(feature);
      });

      const drawFeatures = this.featuresAt(null, bbox, "click");
      const featuresInBox = [...vectorFeatures, ...drawFeatures];
      const idsToSelect = this.getUniqueIds(featuresInBox).filter((id) => !this.isSelected(id));

      if (idsToSelect.length) {
        this.select(idsToSelect);
        idsToSelect.forEach((id) => this.doRender(id));
        this.updateUIClasses({ mouse: constants.cursors.MOVE });
      }
    }
    this.stopExtendedInteractions(state);
  },

  /**
   * Gets the features from the map that are not draw features.
   *
   * NOTE: Since this is getting the features using the queryRenderedFeatures method,
   * it will return feature geometry used by the map for rendering. This means that at
   * high zoom levels, the precision of the geometry may be lower than the original which
   * results in the features returned potentially not being the same as the original.
   */
  getFeatures: function (
    this: MapboxDraw.DrawCustomModeThis,
    event: MapboxDraw.MapMouseEvent,
    bbox?: [mapboxgl.PointLike, mapboxgl.PointLike]
  ): mapboxgl.MapboxGeoJSONFeature[] {
    // Check if there is a vector feature we need to create
    const queryBbox: [mapboxgl.PointLike, mapboxgl.PointLike] = bbox || [
      [event.point.x - 5, event.point.y - 5],
      [event.point.x + 5, event.point.y + 5],
    ];

    return this.map
      ?.queryRenderedFeatures(queryBbox, {
        filter: NBN_SOURCE_LAYER_FILTER,
      })
      .filter(
        (feature: mapboxgl.MapboxGeoJSONFeature) =>
          feature.source === NBN_SOURCE && feature.sourceLayer === NBN_SOURCE_LAYER && !feature.state.isEditing
      );
  },

  /**
   * Adds a draw feature to the drawControl and sets the feature state to isEditing for the underlying
   * vector feature (which styles use to hide the feature behind the draw feature)
   */
  addDrawFeature(this: MapboxDraw.DrawCustomModeThis, feature: mapboxgl.MapboxGeoJSONFeature): MapboxDraw.DrawFeature {
    const drawFeature = this.newFeature(feature);
    const featureId = feature.properties?.id;
    this.map?.setFeatureState({ id: featureId, source: NBN_SOURCE, sourceLayer: NBN_SOURCE_LAYER }, { isEditing: true });
    this.addFeature(drawFeature);

    return drawFeature;
  },
};

export default nbnSelect;
